/**
 * Persistent pilot progression + badge unlocks (localStorage).
 */
import { BADGE_DEFS, BADGE_BY_ID, emptyProgressStats, TIER_COLOR } from './badges.js';

const STORAGE_KEY = 'gs_pilot_progress_v1';

export class PilotProgress {
    constructor() {
        this.stats = emptyProgressStats();
        this.unlocked = {}; // id -> timestamp
        this._listeners = [];
        this.load();
    }

    load() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            this.stats = { ...emptyProgressStats(), ...(data.stats || {}) };
            this.stats.killsByVariant = { ...(data.stats?.killsByVariant || {}) };
            this.stats.planetTypes = { ...(data.stats?.planetTypes || {}) };
            this.stats.abilities = { ...(data.stats?.abilities || {}) };
            this.stats.planetsVisited = Array.isArray(data.stats?.planetsVisited)
                ? data.stats.planetsVisited
                : [];
            this.unlocked = data.unlocked || {};
        } catch {
            /* ignore corrupt save */
        }
    }

    save() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                stats: this.stats,
                unlocked: this.unlocked,
                savedAt: Date.now(),
            }));
        } catch {
            /* quota */
        }
    }

    onUnlock(fn) {
        this._listeners.push(fn);
    }

    /** Apply pilot level from live player when higher */
    syncPilotLevel(player) {
        if (!player) return;
        if ((player.level || 1) > (this.stats.pilotLevel || 1)) {
            this.stats.pilotLevel = player.level;
            this.stats.pilotXp = player.xp || 0;
            this.evaluate();
        }
    }

    /**
     * Patch stats and re-check badges.
     * @param {Partial<object>} patch
     * @returns {object[]} newly unlocked badge defs
     */
    record(patch = {}) {
        const s = this.stats;
        for (const [k, v] of Object.entries(patch)) {
            if (k === 'killsByVariant' && v && typeof v === 'object') {
                for (const [vk, vv] of Object.entries(v)) {
                    s.killsByVariant[vk] = (s.killsByVariant[vk] || 0) + vv;
                }
            } else if (k === 'planetTypes' && v && typeof v === 'object') {
                for (const [pk, pv] of Object.entries(v)) {
                    if (pv) s.planetTypes[pk] = true;
                }
            } else if (k === 'abilities' && v && typeof v === 'object') {
                for (const [ak, av] of Object.entries(v)) {
                    if (av) s.abilities[ak] = true;
                }
            } else if (k === 'planetsVisited' && v) {
                const id = String(v);
                if (!s.planetsVisited.includes(id)) s.planetsVisited.push(id);
            } else if (typeof v === 'number' && typeof s[k] === 'number') {
                s[k] = (s[k] || 0) + v;
            } else if (typeof v === 'number' && s[k] === undefined) {
                s[k] = v;
            } else if (typeof v === 'boolean' || typeof v === 'string') {
                s[k] = v;
            } else if (typeof v === 'number' && (k.startsWith('max') || k === 'pilotLevel')) {
                s[k] = Math.max(s[k] || 0, v);
            }
        }
        // max* fields take absolute max when passed as setMaxMagnet style
        if (patch.setMaxMagnet != null) {
            s.maxMagnet = Math.max(s.maxMagnet || 0, patch.setMaxMagnet);
        }
        if (patch.setPilotLevel != null) {
            s.pilotLevel = Math.max(s.pilotLevel || 1, patch.setPilotLevel);
        }
        // Debounced save so frame-throttled snapshots don't thrash localStorage
        this._dirty = true;
        if (!this._saveTimer) {
            this._saveTimer = setTimeout(() => {
                this._saveTimer = null;
                if (this._dirty) {
                    this._dirty = false;
                    this.save();
                }
            }, 800);
        }
        return this.evaluate();
    }

    evaluate() {
        const newly = [];
        for (const badge of BADGE_DEFS) {
            if (this.unlocked[badge.id]) continue;
            try {
                if (badge.check(this.stats)) {
                    this.unlocked[badge.id] = Date.now();
                    newly.push(badge);
                }
            } catch {
                /* bad check */
            }
        }
        if (newly.length) {
            this.save();
            for (const b of newly) {
                for (const fn of this._listeners) {
                    try { fn(b); } catch { /* */ }
                }
            }
        }
        return newly;
    }

    /** Persist without re-evaluating (call after batch of records if needed). */
    flush() {
        this.save();
    }

    isUnlocked(id) {
        return !!this.unlocked[id];
    }

    unlockedList() {
        return BADGE_DEFS.filter((b) => this.unlocked[b.id]);
    }

    lockedList() {
        return BADGE_DEFS.filter((b) => !this.unlocked[b.id]);
    }

    count() {
        return {
            unlocked: Object.keys(this.unlocked).length,
            total: BADGE_DEFS.length,
        };
    }

    getBadge(id) {
        return BADGE_BY_ID[id] || null;
    }

    tierColor(tier) {
        return TIER_COLOR[tier] || TIER_COLOR.bronze;
    }
}

export { BADGE_DEFS, TIER_COLOR };
